// Blog post metadata. Kept separate from the post bodies (blogContent.ts) so
// the router can list every post's route without pulling the full articles
// into the entry bundle.
//
// To add a post: add its entry here (newest first), add its body to
// blogContent.ts under the same slug, and add its URL to public/sitemap.xml.

import type { ServicePageSlug } from "@/data/servicePageSlugs";

export interface BlogPostMeta {
  slug: string;
  title: string;
  /**
   * Shorter <title> for search results when `title` (the H1) is too long.
   * With the " | HBT Integrisys" suffix, keep the total to 60 characters.
   */
  seoTitle?: string;
  /** Meta description, ideally 140–160 characters. */
  description: string;
  /** Short summary shown on the blog listing and under the post title. */
  excerpt: string;
  category: string;
  /** Publish date, YYYY-MM-DD. */
  date: string;
  readingMinutes: number;
  /** Long-tail SEO phrases for this post. */
  keywords: string[];
  /**
   * The page this post supports and links to from its call-to-action box:
   * a service landing page (servicePages.ts slug) or the training page.
   */
  relatedPage: ServicePageSlug | "training";
}

export const BLOG_AUTHOR = "HBT Integrisys Team";

export const BLOG_POSTS: BlogPostMeta[] = [
  {
    slug: "how-to-hire-offshore-react-and-net-developers-from-india",
    relatedPage: "dedicated-development-teams",
    title: "How to Hire Offshore React and .NET Developers from India",
    seoTitle: "Hiring Offshore React & .NET Developers",
    description:
      "A practical guide to hiring offshore React and .NET developers from India — engagement models, evaluation, time zones, real costs and protecting your IP.",
    excerpt:
      "Experienced engineers in weeks instead of months — if you choose the right model, evaluate developers properly and plan communication up front.",
    category: "Hiring & Teams",
    date: "2026-10-04",
    readingMinutes: 6,
    keywords: [
      "hire React developer in India",
      "hire .NET developers offshore",
      "how to hire offshore developers from India",
      "dedicated React and .NET developers for startups",
      "offshore software development team time zone overlap",
    ],
  },
  {
    slug: "tamil-nadu-payroll-compliance-checklist-for-small-businesses",
    relatedPage: "hr-outsourcing-payroll",
    title: "Payroll Compliance Checklist for Small Businesses in Tamil Nadu",
    seoTitle: "Tamil Nadu Payroll Compliance Checklist",
    description:
      "A payroll compliance checklist for small businesses in Tamil Nadu — registrations, PF, ESI, professional tax, TDS, monthly and annual tasks and common mistakes.",
    excerpt:
      "Registrations, monthly deductions and deposits, annual filings and records — the payroll compliance items most Tamil Nadu SMEs need to manage.",
    category: "HR & Payroll",
    date: "2026-10-04",
    readingMinutes: 5,
    keywords: [
      "payroll compliance checklist Tamil Nadu",
      "payroll management services in Tamil Nadu",
      "PF ESI and professional tax for small businesses",
      "monthly payroll process for SMEs in India",
      "when to outsource payroll for a small business",
    ],
  },
  {
    slug: "cloud-migration-planning-guide-for-small-businesses",
    relatedPage: "it-consulting-cloud",
    title: "Cloud Migration Planning Guide for Small and Medium Businesses",
    seoTitle: "Cloud Migration Planning Guide for SMEs",
    description:
      "Plan a smooth move to Azure or AWS: inventory your systems, pick a migration approach, estimate costs, secure your data and migrate in low-risk waves.",
    excerpt:
      "Moving off an office server or old hosting? Here's how to plan a cloud migration without downtime, surprise bills or security gaps.",
    category: "Cloud & IT Consulting",
    date: "2026-10-04",
    readingMinutes: 5,
    keywords: [
      "cloud migration planning for small businesses",
      "cloud and DevOps migration consulting",
      "migrate on-premise server to Azure or AWS",
      "lift and shift vs replatform vs refactor",
      "cloud migration checklist for SMEs",
    ],
  },
  {
    slug: "how-to-become-a-full-stack-developer-in-nagercoil",
    relatedPage: "training",
    title: "How to Become a Full Stack Developer in Nagercoil: A Practical Roadmap",
    seoTitle: "Become a Full Stack Developer in Nagercoil",
    description:
      "A step-by-step roadmap to becoming a full stack developer from Nagercoil — web basics, React or Angular, a backend stack, databases, projects and timelines.",
    excerpt:
      "From HTML and JavaScript to React, a backend stack and deployed projects — a realistic path to a full stack developer job, without leaving Kanyakumari.",
    category: "Careers & Training",
    date: "2026-10-04",
    readingMinutes: 5,
    keywords: [
      "full stack developer course in Nagercoil",
      "how to become a full stack developer",
      "full stack developer roadmap for freshers",
      "software developer jobs for freshers in Kanyakumari",
      "learn React and Java Spring Boot in Nagercoil",
    ],
  },
  {
    slug: "web-app-vs-mobile-app-which-to-build-first",
    relatedPage: "mobile-app-development",
    title: "Web App vs Mobile App: Which Should Your Business Build First?",
    seoTitle: "Web App vs Mobile App: Which to Build First",
    description:
      "Deciding between a web app and a mobile app? Compare cost, reach, speed to market and user needs to choose the right first build for your business.",
    excerpt:
      "A practical way to decide whether your product should start as a web application, a mobile app, or both — based on your users, budget and timeline.",
    category: "App Development",
    date: "2026-09-27",
    readingMinutes: 6,
    keywords: [
      "web app vs mobile app for small business",
      "should I build a web app or mobile app first",
      "cost of mobile app development in India",
      "progressive web app vs native app",
      "cross-platform app development with React Native",
    ],
  },
  {
    slug: "dedicated-development-team-vs-in-house-hiring",
    relatedPage: "dedicated-development-teams",
    title: "Dedicated Development Team vs In-House Hiring: How to Decide",
    seoTitle: "Dedicated Dev Team vs In-House Hiring",
    description:
      "In-house developers or a dedicated development team? Compare cost, hiring time, flexibility and control to pick the right model for your next project.",
    excerpt:
      "Both models can work well. Here's how to weigh cost, speed, control and long-term ownership before you commit to one.",
    category: "Hiring & Teams",
    date: "2026-09-27",
    readingMinutes: 7,
    keywords: [
      "dedicated development team vs in-house developers",
      "benefits of hiring a dedicated development team",
      "offshore software development team from India",
      "how to scale an engineering team quickly",
      "IT staff augmentation vs in-house hiring",
    ],
  },
  {
    slug: "how-to-plan-a-custom-web-application-project",
    relatedPage: "web-development",
    title: "How to Plan a Custom Web Application Project: A Step-by-Step Guide",
    seoTitle: "Planning a Custom Web Application Project",
    description:
      "Planning a custom web application? Follow this step-by-step guide to define scope, choose a tech stack, set a budget and work with a development partner.",
    excerpt:
      "Most web projects that go over budget were under-planned. This guide walks through what to decide before a single line of code is written.",
    category: "Web Development",
    date: "2026-09-27",
    readingMinutes: 8,
    keywords: [
      "how to plan a custom web application",
      "custom web application development process",
      "choosing a tech stack for a web application",
      "web application project requirements checklist",
      "working with a web development company in India",
    ],
  },
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * "2026-09-27" -> "27 September 2026". Formatted by hand rather than with
 * toLocaleDateString, so the prerendered HTML and the client's first render
 * can't disagree because of time zones or ICU data.
 */
export function formatPostDate(date: string) {
  const [year, month, day] = date.split("-").map(Number);
  return `${day} ${MONTHS[month - 1]} ${year}`;
}
